import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

// --- Type Definitions ---
export interface IServiceDetailsAccordion {
  Question: string;
  Answer: string;
}
export interface IServiceDetailsStep {
  Step: string;
  Title: string;
  Text: string;
}
export interface IServiceDetailsProperties {
  ExtraParagraph: string;
  Accordion: IServiceDetailsAccordion[];
  Steps: IServiceDetailsStep[];
}

export interface IServicePageFilter {
    Title: string;
    Link: string;
  }

export interface IServiceDetailsWidget {
  // Assuming IWidgetType is defined elsewhere
  type: string; // should be WidgetType.ServiceDetails in parent
  order: number;
  Title: string;
  Header: string;
  Description: string;
  ImageUrl: string;
  Properties: IServiceDetailsProperties;
  Filters: IServicePageFilter[];
}

// --- Props for the ServiceDetailsItem component ---
interface ServiceDetailsItemProps {
  item: IServiceDetailsWidget;
  error: any;
  onChange: (updatedItem: IServiceDetailsWidget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const ServiceDetailsItem: React.FC<ServiceDetailsItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Top-level fields
  const widgetTitle: string = item.Title || "";
  const widgetHeader: string = item.Header || "";
  const widgetDescription: string = item.Description || "";
  const widgetImage: string = item.ImageUrl || "";

  // Properties section (ensure defaults)
  const properties = item.Properties || {
    ExtraParagraph: "",
    Accordion: [],
    Steps: [],
  };
  const { ExtraParagraph, Accordion, Steps } = properties;
  const filters = item.Filters || [];

  // Local state for main image picker
  const [mainImagePickerOpen, setMainImagePickerOpen] = useState<boolean>(false);

  // For dynamic lists, no local state is needed except for tracking changes via onChange
  // Helper: extract value from an event or use the value directly.
  const getValue = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ): string => (typeof e === "string" ? e : e.target.value);

  // --- Handlers for top-level fields ---
  const handleTitleChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  const handleHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Header: getValue(e) }, "Header");
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Description: getValue(e) }, "Description");
  };

  const handleImageChange = (selected: string | string[]) => {
    const newImage = Array.isArray(selected) ? selected[0] : selected;
    onChange({ ...item, ImageUrl: newImage }, "ImageUrl");
    setMainImagePickerOpen(false);
  };

  // --- Properties Handlers ---
  const handleExtraParagraphChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange(
      { ...item, Properties: { ...properties, ExtraParagraph: getValue(e) } },
      "Properties"
    );
  };

  // Accordion Handlers
  const handleAccordionChange = (
    index: number,
    field: keyof IServiceDetailsAccordion,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedAccordion = [...Accordion];
    updatedAccordion[index] = { ...updatedAccordion[index], [field]: newValue };
    onChange({ ...item, Properties: { ...properties, Accordion: updatedAccordion } }, "Properties");
  };

  const handleAddAccordion = () => {
    const newEntry: IServiceDetailsAccordion = { Question: "", Answer: "" };
    const updatedAccordion = [...Accordion, newEntry];
    onChange({ ...item, Properties: { ...properties, Accordion: updatedAccordion } }, "Properties");
  };

  const handleRemoveAccordion = (index: number) => {
    const updatedAccordion = Accordion.filter((_, i) => i !== index);
    onChange({ ...item, Properties: { ...properties, Accordion: updatedAccordion } }, "Properties");
  };

  // Steps Handlers
  const handleStepChange = (
    index: number,
    field: keyof IServiceDetailsStep,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedSteps = [...Steps];
    updatedSteps[index] = { ...updatedSteps[index], [field]: newValue };
    onChange({ ...item, Properties: { ...properties, Steps: updatedSteps } }, "Properties");
  };

  const handleAddStep = () => {
    const newStep: IServiceDetailsStep = { Step: "", Title: "", Text: "" };
    const updatedSteps = [...Steps, newStep];
    onChange({ ...item, Properties: { ...properties, Steps: updatedSteps } }, "Properties");
  };

  const handleRemoveStep = (index: number) => {
    const updatedSteps = Steps.filter((_, i) => i !== index);
    onChange({ ...item, Properties: { ...properties, Steps: updatedSteps } }, "Properties");
  };

    function handleGalleryImageSelect(selected: string | string[]): void {
        throw new Error("Function not implemented.");
    }

    const handleFilterChange = (
        index: number,
        field: keyof IServicePageFilter,
        e: React.ChangeEvent<HTMLInputElement> | string
      ) => {
        const newValue = getValue(e);
        const updatedFilters = [...filters];
        updatedFilters[index] = { ...updatedFilters[index], [field]: newValue };
        onChange({ ...item, Filters: updatedFilters }, "Filters");
      };
    
      const handleAddFilter = () => {
        const newFilter: IServicePageFilter = { Title: "", Link: "" };
        const updatedFilters = [...filters, newFilter];
        onChange({ ...item, Filters: updatedFilters }, "Filters");
      };
    
      const handleRemoveFilter = (index: number) => {
        const updatedFilters = filters.filter((_, i) => i !== index);
        onChange({ ...item, Filters: updatedFilters }, "Filters");
      };
    

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Service Details Widget
        </Typography>

        {/* Top-level Fields */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={widgetTitle}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Header"
          title="Header"
          value={widgetHeader}
          error={error?.Header || ""}
          onChange={(e) => handleHeaderChange(e)}
          onBlur={() => onblur("Header")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Description"
          title="Description"
          value={widgetDescription}
          error={error?.Description || ""}
          onChange={(e) => handleDescriptionChange(e)}
          onBlur={() => onblur("Description")}
          disabled={false}
        />

        {/* Main Image with Image Picker */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Main Image
        </Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            height: 150,
            width: 250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9f9f9",
            cursor: "pointer",
            mb: 2,
            overflow: "hidden",
          }}
          onClick={() => setMainImagePickerOpen(true)}
        >
          {widgetImage ? (
            <img
              src={widgetImage}
              alt="Service Details"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Select Main Image
            </Typography>
          )}
        </Box>
        {error?.ImageUrl && (
          <Typography variant="body2" color="error">
            {error.ImageUrl}
          </Typography>
        )}

        {/* Properties: ExtraParagraph */}
        <GenericTextAreaInput
          name="ExtraParagraph"
          title="Extra Paragraph"
          value={properties.ExtraParagraph}
          error={error?.Properties?.ExtraParagraph || ""}
          onChange={(e) => handleExtraParagraphChange(e)}
          onBlur={() => onblur("ExtraParagraph")}
          disabled={false}
        />

        {/* Accordion Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Accordion (Q&A)
        </Typography>
        {Accordion.map((entry, idx) => (
          <Box key={idx} sx={{ border: "1px solid #ddd", p: 2, borderRadius: "4px", mb: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`AccordionQuestion-${idx}`}
                  title="Question"
                  value={entry.Question}
                  error={error?.Properties?.Accordion?.[idx]?.Question || ""}
                  onChange={(e) => handleAccordionChange(idx, "Question", e)}
                  onBlur={() => onblur("AccordionQuestion", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`AccordionAnswer-${idx}`}
                  title="Answer"
                  value={entry.Answer}
                  error={error?.Properties?.Accordion?.[idx]?.Answer || ""}
                  onChange={(e) => handleAccordionChange(idx, "Answer", e)}
                  onBlur={() => onblur("AccordionAnswer", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <IconButton color="error" onClick={() => handleRemoveAccordion(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddAccordion} sx={{ mb: 2 }}>
          Add Accordion Entry
        </Button>

        {/* Steps Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Steps
        </Typography>
        {Steps.map((step, idx) => (
          <Box key={idx} sx={{ border: "1px solid #ddd", p: 2, borderRadius: "4px", mb: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <GenericInput
                  type="text"
                  name={`Step-${idx}`}
                  title="Step"
                  value={step.Step}
                  error={error?.Properties?.Steps?.[idx]?.Step || ""}
                  onChange={(e) => handleStepChange(idx, "Step", e)}
                  onBlur={() => onblur("Step", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GenericInput
                  type="text"
                  name={`StepTitle-${idx}`}
                  title="Step Title"
                  value={step.Title}
                  error={error?.Properties?.Steps?.[idx]?.Title || ""}
                  onChange={(e) => handleStepChange(idx, "Title", e)}
                  onBlur={() => onblur("StepTitle", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GenericInput
                  type="text"
                  name={`StepText-${idx}`}
                  title="Step Text"
                  value={step.Text}
                  error={error?.Properties?.Steps?.[idx]?.Text || ""}
                  onChange={(e) => handleStepChange(idx, "Text", e)}
                  onBlur={() => onblur("StepText", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <IconButton color="error" onClick={() => handleRemoveStep(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddStep} sx={{ mb: 2 }}>
          Add Step
        </Button>

        {/* Filters Section (Optional, if you want to include filters) */}

        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Filters
        </Typography>
        {filters.map((filter, idx) => (
          <Box key={idx} sx={{ border: "1px solid #ddd", p: 2, borderRadius: "4px", mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`FilterTitle-${idx}`}
                  title="Filter Title"
                  value={filter.Title}
                  error={error?.Filters?.[idx]?.Title || ""}
                  onChange={(e) => handleFilterChange(idx, "Title", e)}
                  onBlur={() => onblur("FilterTitle", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`FilterLink-${idx}`}
                  title="Filter Link"
                  value={filter.Link}
                  error={error?.Filters?.[idx]?.Link || ""}
                  onChange={(e) => handleFilterChange(idx, "Link", e)}
                  onBlur={() => onblur("FilterLink", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton color="error" onClick={() => handleRemoveFilter(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFilter}>
          Add Filter
        </Button>

      </CardContent>
      {mainImagePickerOpen && (
        <ImagePicker
          open={mainImagePickerOpen}
          multiSelect={false}
          onClose={() => setMainImagePickerOpen(false)}
          onSelect={handleGalleryImageSelect}
        />
      )}
    </Card>
  );
};

export default ServiceDetailsItem;
